import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Thanks for subscribing! You\'ll receive property alerts and market updates.');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('You\'re already subscribed to our newsletter!');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const footerLinks = {
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Contact Us', path: '/contact' },
      { name: 'FAQ', path: '/faq' },
      { name: 'Agent Zone', path: '/auth' }
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy-policy' },
      { name: 'Terms & Conditions', path: '/terms' },
      { name: 'Cookie Policy', path: '/cookie-policy' },
      { name: 'PAIA Manual', path: '/paia-manual' }
    ],
    properties: [
      { name: 'Properties for Sale', path: '/properties?type=For%20Sale' },
      { name: 'Properties to Rent', path: '/properties?type=To%20Rent' },
      { name: 'Find Contractors', path: '/home-services' },
      { name: 'Bond Calculator', path: '/bond-calculator' }
    ],
    popularAreas: [
      { name: 'Sandton', path: '/houses-for-sale/sandton' },
      { name: 'Cape Town', path: '/houses-for-sale/cape-town' },
      { name: 'Umhlanga', path: '/houses-for-sale/umhlanga' },
      { name: 'Pretoria', path: '/houses-for-sale/pretoria' },
      { name: 'Midrand', path: '/apartments-to-rent/midrand' },
      { name: 'Durban', path: '/property-for-sale/durban' }
    ],
    provinces: [
      { name: 'Gauteng', path: '/properties?province=gauteng' },
      { name: 'Western Cape', path: '/properties?province=western-cape' },
      { name: 'KwaZulu-Natal', path: '/properties?province=kwazulu-natal' },
      { name: 'Eastern Cape', path: '/properties?province=eastern-cape' }
    ]
  };

  const socialLinks = [
    {
      name: 'Facebook',
      url: 'https://www.facebook.com/share/1DKt5oA7Uu/',
      icon: 'fab fa-facebook-f',
      color: 'hover:bg-blue-600'
    },
    {
      name: 'WhatsApp',
      url: 'https://wa.me/27655249286',
      icon: 'fab fa-whatsapp',
      color: 'hover:bg-green-500'
    },
    {
      name: 'TikTok',
      url: 'https://www.tiktok.com/@flippyhomes?_r=1&_t=ZS-94AZfs0gMbA',
      icon: 'fab fa-tiktok',
      color: 'hover:bg-black'
    }
  ];

  return (
    <footer className="bg-gray-900 text-gray-300" role="contentinfo" itemScope itemType="https://schema.org/WPFooter">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <h2 className="text-2xl font-bold">
                <span className="text-white">Flippy</span>
                <span className="text-emerald-500">Homes</span>
                <span className="text-emerald-400 text-sm">.co.za</span>
              </h2>
            </Link>
            <p className="text-sm text-gray-400 mb-4">
              South Africa's trusted property marketplace. Find your perfect home, 
              connect with agents, and discover reliable home service contractors.
            </p>
            
            {/* Social Media Links */}
            <div className="flex gap-3 mb-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center transition-all duration-300 ${social.color} hover:text-white hover:scale-110`}
                  aria-label={`Follow Flippy Homes on ${social.name}`}
                  title={`Follow us on ${social.name}`}
                  data-testid={`social-${social.name.toLowerCase()}`}
                >
                  {social.name === 'Facebook' && (
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  )}
                  {social.name === 'WhatsApp' && (
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  )}
                  {social.name === 'TikTok' && (
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                  )}
                </a>
              ))}
            </div>

            {/* Newsletter Signup */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-white font-semibold text-sm mb-2">
                <i className="fas fa-envelope mr-2 text-emerald-400"></i>
                Stay Updated
              </h3>
              <p className="text-xs text-gray-400 mb-3">
                Get property alerts & market updates
              </p>
              <form onSubmit={handleNewsletterSubmit} className="space-y-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500 transition-colors"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-800 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="newsletter-submit-btn"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Subscribing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Subscribe
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Company</h3>
            <ul className="space-y-2" role="list">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Legal</h3>
            <ul className="space-y-2" role="list">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Properties Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Properties</h3>
            <ul className="space-y-2" role="list">
              {footerLinks.properties.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Areas */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Popular Areas</h3>
            <ul className="space-y-2" role="list">
              {footerLinks.popularAreas.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info Bar */}
        <div className="mt-10 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap justify-center md:justify-start gap-6 text-sm">
              <a 
                href="https://wa.me/27655249286" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors"
              >
                <i className="fab fa-whatsapp text-green-500"></i>
                <span>+27 65 524 9286</span>
              </a>
              <a 
                href="mailto:info@flippyhomes.co.za"
                className="flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors"
              >
                <i className="fas fa-envelope text-emerald-500"></i>
                <span>info@flippyhomes.co.za</span>
              </a>
              <span className="flex items-center gap-2 text-gray-400">
                <i className="fas fa-map-marker-alt text-emerald-500"></i>
                <span>South Africa</span>
              </span>
            </div>
            
            {/* App Download Placeholder */}
            <div className="flex gap-3">
              <a 
                href="#" 
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors"
                aria-label="Download on App Store"
              >
                <i className="fab fa-apple text-xl"></i>
                <div className="text-left">
                  <div className="text-[10px] text-gray-400">Download on the</div>
                  <div className="text-sm font-semibold text-white">App Store</div>
                </div>
              </a>
              <a 
                href="#" 
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors"
                aria-label="Get it on Google Play"
              >
                <i className="fab fa-google-play text-xl"></i>
                <div className="text-left">
                  <div className="text-[10px] text-gray-400">Get it on</div>
                  <div className="text-sm font-semibold text-white">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-gray-950 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-sm">
            <p className="text-gray-500">
              © {currentYear} Flippy Homes (Pty) Ltd. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-gray-500">
              <Link to="/privacy-policy" className="hover:text-emerald-400 transition-colors">Privacy</Link>
              <span className="text-gray-700">|</span>
              <Link to="/terms" className="hover:text-emerald-400 transition-colors">Terms</Link>
              <span className="text-gray-700">|</span>
              <Link to="/cookie-policy" className="hover:text-emerald-400 transition-colors">Cookies</Link>
              <span className="text-gray-700">|</span>
              <a 
                href="https://wa.me/27655249286" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-emerald-400 transition-colors flex items-center gap-1"
              >
                <i className="fab fa-whatsapp text-green-500"></i>
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* SEO Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          "name": "Flippy Homes",
          "url": "https://flippyhomes.co.za",
          "logo": "https://flippyhomes.co.za/logo.png",
          "description": "South Africa's trusted property marketplace. Find homes for sale and rent.",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "ZA"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+27-65-524-9286",
            "contactType": "customer service",
            "availableLanguage": ["English", "Afrikaans"]
          },
          "sameAs": [
            "https://www.facebook.com/share/1DKt5oA7Uu/",
            "https://www.tiktok.com/@flippyhomes"
          ]
        })
      }} />
    </footer>
  );
};

export default Footer;
